import React from 'react';
import {
  Syringe,
  ShieldCheck,
  Calendar,
  AlertCircle,
  Bug,
  Pill,
  CheckCircle2,
  ArrowRight,
  Info,
  Clock,
  Shield,
  CalendarDays,
  AlertTriangle,
  Timer
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

/* 
  Subcomponente de Vacunas
*/
const VaccinationSchedule = ({ schedule, title, isAdult = false }) => (
  <div className="space-y-4">
    {/* Encabezado */}
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
        <Calendar className="h-5 w-5 text-blue-500" />
        {title}
      </h3>
      <div className="flex items-center gap-2">
        <Shield className="h-5 w-5 text-blue-500" />
        <span className="text-sm text-blue-600 font-medium">
          {isAdult ? 'Protección Anual' : 'Protección Inicial'}
        </span>
      </div>
    </div>

    <div className="space-y-4">
      {isAdult ? (
        /* Vacunas de etapa adulta (anuales) */
        schedule.map((vaccine, idx) => (
          <div
            key={idx}
            className="bg-gradient-to-br from-blue-50 to-white rounded-lg p-3 border border-blue-100 hover:shadow-sm transition-all"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Syringe className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <h4 className="text-base sm:text-lg font-medium text-gray-900">
                    {vaccine.vacuna}
                  </h4>
                  <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium gap-1.5">
                    <CalendarDays className="h-4 w-4" />
                    {vaccine.cuando}
                  </span>
                </div>
                <p className="mt-2 text-base text-gray-700 bg-blue-50/50 p-2 rounded-lg flex items-start gap-2">
                  <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  {vaccine.detalle}
                </p>
              </div>
            </div>
          </div>
        ))
      ) : (
        /* Vacunas de etapa cachorro */
        <div className="relative">
          {schedule.map((period, idx) => (
            <div key={idx} className="relative space-y-4 mb-4">
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-lg p-3 border border-blue-100 space-y-4">
                {/* Bloque de cada periodo (ej. 6-8 semanas) */}
                <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-blue-50">
                  <Timer className="h-5 w-5 text-blue-600" />
                  <span className="text-base text-gray-900 font-medium">
                    {period.cuando}
                  </span>
                </div>

                {period.vacunas.map((vaccine, vIdx) => (
                  <div
                    key={vIdx}
                    className="bg-white rounded-lg p-3 hover:shadow-sm transition-all space-y-3 border border-blue-50"
                  >
                    {/* Título de vacuna e importancia */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Syringe className="h-5 w-5 text-blue-600" />
                        </div>
                        <h4 className="text-base sm:text-lg font-medium text-gray-900">
                          {vaccine.nombre}
                        </h4>
                      </div>
                      {vaccine.importancia && (
                        <span className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium gap-1.5">
                          <AlertTriangle className="h-4 w-4" />
                          {vaccine.importancia}
                        </span>
                      )}
                    </div>

                    {/* Qué esperar */}
                    {vaccine.queEsperar && (
                      <div className="space-y-2 bg-blue-50/50 p-3 rounded-lg">
                        <h5 className="text-base text-blue-700 font-medium flex items-center gap-2">
                          <Info className="h-4 w-4" />
                          Qué esperar:
                        </h5>
                        {vaccine.queEsperar.map((expect, eIdx) => (
                          <div
                            key={eIdx}
                            className="flex items-start gap-2 text-base text-gray-700"
                          >
                            <ArrowRight className="h-4 w-4 mt-0.5 text-blue-500" />
                            <span>{expect}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Consejo */}
                    {vaccine.consejo && (
                      <div className="flex items-start gap-2 bg-orange-50 p-3 rounded-lg border border-orange-100">
                        <AlertCircle className="h-4 w-4 mt-0.5 text-orange-500" />
                        <p className="text-base text-gray-700">
                          {vaccine.consejo}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Línea vertical entre secciones (opcional) */}
              {idx < schedule.length - 1 && (
                <div className="absolute left-5 top-full h-4 w-0.5 bg-blue-200" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

/* 
  Subcomponente de Desparasitación
*/
const DewormingSection = ({ data, title, icon: Icon, color = 'emerald' }) => (
  <div
    className={`bg-gradient-to-br from-${color}-50 to-white rounded-lg p-3 border border-${color}-100 hover:shadow-sm transition-all`}
  >
    <div className="flex items-start gap-3">
      <div className={`p-2 bg-${color}-100 rounded-lg`}>
        <Icon className={`h-5 w-5 text-${color}-600`} />
      </div>

      <div className="flex-1 min-w-0 space-y-3">
        {/* Título */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h4 className="text-base sm:text-lg font-medium text-gray-900">
            {title}
          </h4>
          <div className="inline-flex items-center px-3 py-1 bg-white rounded-full border border-gray-100">
            <Clock className={`h-4 w-4 text-${color}-500`} />
            <span className="text-sm text-gray-600 ml-1.5">
              Frecuencia: <span className="font-medium">{data.frecuencia}</span>
            </span>
          </div>
        </div>

        {/* Recomendación principal */}
        {data.recomendacion && (
          <div
            className={`flex items-start gap-2 bg-${color}-50/50 p-3 rounded-lg border border-${color}-100`}
          >
            <Info className={`h-4 w-4 mt-0.5 text-${color}-600`} />
            <p className="text-base text-gray-700">{data.recomendacion}</p>
          </div>
        )}

        {/* Zonas importantes o Tips */}
        {(data.zonasImportantes || data.tips) && (
          <div className="space-y-3 bg-white p-3 rounded-lg border border-gray-100">
            <div className="flex items-center gap-2">
              <CheckCircle2 className={`h-5 w-5 text-${color}-600`} />
              <h5 className="text-base text-gray-900 font-medium">
                {data.zonasImportantes
                  ? 'Zonas importantes:'
                  : 'Tips importantes:'}
              </h5>
            </div>
            <div className="space-y-2 pl-4">
              {(data.zonasImportantes || data.tips).map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 group">
                  <ArrowRight
                    className={`h-4 w-4 mt-0.5 text-${color}-400 group-hover:translate-x-0.5 transition-transform`}
                  />
                  <span className="text-base text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Consejo final */}
        {data.consejo && (
          <div
            className={`flex items-start gap-2 bg-${color}-50 p-3 rounded-lg border border-${color}-100`}
          >
            <Info className={`h-4 w-4 mt-0.5 text-${color}-500`} />
            <p className="text-base text-gray-700">{data.consejo}</p>
          </div>
        )}
      </div>
    </div>
  </div>
);

/* 
  Componente principal: PreventiveCareCard
*/
const PreventiveCareCard = ({ planPreventivo }) => {
  if (!planPreventivo) return null;

  return (
    <Card className="overflow-hidden bg-white">
      {/* Encabezado del Card */}
      <CardHeader className="border-b bg-gradient-to-r from-blue-500/10 to-transparent p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-sm">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl text-gray-800 font-semibold">
              Plan Preventivo
            </CardTitle>
            <p className="text-base text-gray-500 mt-1">
              Mantén a tu mascota saludable y protegida
            </p>
          </div>
        </div>
      </CardHeader>

      {/* Contenido del Card */}
      <CardContent className="p-4 space-y-4">
        {/* Alerta descriptiva */}
        <Alert className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <AlertCircle className="h-5 w-5 text-blue-500" />
          <AlertDescription className="text-base text-blue-700">
            {planPreventivo.vacunas.descripcion}
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          {/* Sección de Vacunas */}
          <div className="space-y-4">
            <VaccinationSchedule
              schedule={planPreventivo.vacunas.etapaCachorro}
              title="Vacunas - Etapa Cachorro"
            />
            <VaccinationSchedule
              schedule={planPreventivo.vacunas.etapaAdulta.anuales}
              title="Vacunas - Etapa Adulta"
              isAdult={true}
            />
          </div>

          {/* Sección de Desparasitación */}
          <div className="space-y-3">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <Bug className="h-5 w-5 text-emerald-500" />
              Plan de Desparasitación
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DewormingSection
                data={planPreventivo.desparasitacion.externa}
                title="Desparasitación Externa"
                icon={Bug}
                color="emerald"
              />
              <DewormingSection
                data={planPreventivo.desparasitacion.interna}
                title="Desparasitación Interna"
                icon={Pill}
                color="blue"
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PreventiveCareCard;
